import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import {
  AccountCircle,
  Badge,
  Beenhere,
  Hive,
  Logout,
  ReceiptLong,
} from "@mui/icons-material";
import {
  HbrUsersTable,
  TableContratos,
  TableCompanies,
  TableBarragem,
  Estatisticas,
  TableManchas
} from "../../components";
import { useNavigate, useParams } from "react-router-dom";
import TriangleIcon from "../../components/mapa/icons/triangle";
import { useUserContext } from "../../context";
import Logo from "../../assets/logoSemFundo.png";
import { HBRGISTM } from "../GISTM";

type ViewTypes = "user" | "contratos" | "empreendimento" | "barramento" | "home" | "manchas" | "GISTM";

export const HBRHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { view } = useParams<{ view: ViewTypes; }>();

  const hbrUser = useUserContext();
  const userName = (!hbrUser || "error" in hbrUser) ? undefined : hbrUser.nome.split(" ")[0];

  const [tipo, setTipo] = useState<ViewTypes>('home');

  useEffect(() => {
    if (view) setTipo(view);
  }, [view]);

  const handleClick = (view: ViewTypes) => {
    setTipo(view);
    navigate(`/app/hbr/home/${view}`);
  };

  const handleLogout = () => {
    window.localStorage.removeItem("authToken");
    navigate("/login");
  };

  return (
    <Box sx={{ height: "100vh" }}>

      {/* Título e Items disponíveis */}
      <Paper sx={{
        gap: "0.5em",
        display: "flex",
        flexDirection: "row",
        padding: "15px",
        alignItems: "center",
      }}>
        {/* Logo */}
        <img
          onClick={() => handleClick('home')}
          src={Logo}
          alt={"logo"}
          style={{
            cursor: "pointer",
            objectFit: "contain",
            height: `2.5em`,
            width: `2.5em`,
          }}
        />
        <Divider orientation="vertical" flexItem />

        <Box sx={{
          flex: 1,
          gap: "0.5em",
          display: "flex",
          flexDirection: "row",
          overflowX: "auto",
        }}>
          <ItemButton
            text="Contratos"
            icon={<ReceiptLong fontSize="small" />}
            onClick={() => handleClick("contratos")}
            selected={tipo == 'contratos'}
          />

          <ItemButton
            text="Empreendimentos"
            icon={<Badge fontSize="small" />}
            onClick={() => handleClick("empreendimento")}
            selected={tipo == 'empreendimento'}
          />

          <ItemButton
            text="Barramentos"
            icon={<TriangleIcon
              fontSize="small"
              sx={{
                width: "15px",
                height: "15px",
                display: 'inline-block',
                transform: 'rotate(180deg)',
              }}
            />}
            onClick={() => handleClick("barramento")}
            selected={tipo == 'barramento'}
          />

          <ItemButton
            text="Manchas"
            icon={<Hive fontSize="small" />}
            onClick={() => handleClick("manchas")}
            selected={tipo == 'manchas'}
          />

          <ItemButton
            text="GISTM"
            icon={<Beenhere fontSize="small" />}
            onClick={() => handleClick("GISTM")}
            selected={tipo == 'GISTM'}
          />
        </Box>

        <ItemButton
          text={userName ? userName : "Usuários"}
          icon={<AccountCircle />}
          onClick={() => handleClick("user")}
          selected={tipo == 'user'}
        />

        <ItemButton
          text={"Logout"}
          icon={<Logout />}
          onClick={handleLogout}
          selected={false}
        />
      </Paper>

      {/* Espaçador */}
      <div style={{ height: "15px", flexShrink: 0 }} />

      {/* Item a se exibir */}
      <Box sx={{
        flex: 1,
        width: "100%",
        height: "calc(100vh - 100px)",
      }}>
        <SelectScreen tipo={tipo ?? "contratos"} />
      </Box>
    </Box>
  );
};

interface ItemButtonProps {
  text: string;
  selected: boolean;
  icon: React.ReactNode;
  width?: string | number;
  onClick?: () => void;
}
const ItemButton: React.FC<ItemButtonProps> = ({ text, onClick, selected, icon, width }) => {
  const theme = useTheme();
  const color = selected ? theme.palette.primary.main : theme.palette.background.default;
  const textColor = theme.palette.getContrastText(color);

  return <Box
    sx={{
      gap: "0.5em",
      display: "flex",
      flexDirection: 'row',
      width: width ?? "156px",
      paddingX: "5px",
      paddingY: "5px",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "5px",
      cursor: "pointer",
      border: "1px solid black",
      backgroundColor: color,
      color: textColor,
    }}
    onClick={selected ? undefined : onClick}
  >
    <Typography
      fontWeight={"bold"}
      fontSize={12}
    >
      {text}
    </Typography>
    {icon}
  </Box>;
};

interface SelectItems { tipo: ViewTypes; }
const SelectScreen: React.FC<SelectItems> = ({ tipo }) => {
  switch (tipo) {
    case "user":
      return <Box sx={{ padding: "0.5em", width: "100%", height: "100%" }}>
        <HbrUsersTable isAddUser />
      </Box>;
    case "contratos":
      return <TableContratos isAddNew />;
    case "empreendimento":
      return <Paper elevation={0} sx={{ padding: "0.5em", width: "100%", height: "100%" }}>
        <TableCompanies isAddNew filter />
      </Paper>;
    case "barramento":
      return <Paper elevation={0} sx={{ padding: "0.5em", width: "100%", height: "100%" }}>
        <TableBarragem isAddNew />
      </Paper>;
    case "manchas":
      return <Paper elevation={0} sx={{ padding: "0.5em", width: "100%", height: "100%" }}>
        <TableManchas isAddNew />
      </Paper>;
    case "home":
      return <Box sx={{ padding: "0.5em", width: "100%", height: "100%" }}>
        <Estatisticas />
      </Box>;
    case "GISTM":
      return <Box sx={{ padding: "0.5em", width: "100%", height: "100%" }}>
        <HBRGISTM />
      </Box>;
    default:
      return <Estatisticas />;
  }
};
